import { useEffect, useState } from 'react';
import { timeSince, type TimeSinceOptions } from '@ais-ui/utils';

const REFRESH_INTERVAL_MS = 5000;

/**
 * A single shared ticker drives every `useTimeSince` label. This keeps all
 * relative-time labels updating in lockstep (no drift between them) and means
 * we run exactly one timer no matter how many labels are on screen — instead of
 * one `setInterval` per component instance.
 */
const subscribers = new Set<() => void>();
let intervalId: ReturnType<typeof setInterval> | null = null;

const subscribe = (onTick: () => void): (() => void) => {
  subscribers.add(onTick);

  if (intervalId === null) {
    intervalId = setInterval(() => {
      subscribers.forEach((notify) => notify());
    }, REFRESH_INTERVAL_MS);
  }

  return () => {
    subscribers.delete(onTick);
    if (subscribers.size === 0 && intervalId !== null) {
      clearInterval(intervalId);
      intervalId = null;
    }
  };
};

/**
 * Live, human-readable "time since" label for a date (e.g. "5 minutes ago").
 *
 * Refreshes on the shared ticker so the label stays accurate between data
 * updates, and returns `null` when no date is given so callers can render
 * nothing. No timer is started for empty labels. Formatting is delegated to
 * `timeSince`, so callers can tweak the output via {@link TimeSinceOptions}.
 */
export const useTimeSince = (
  date: Date | null | undefined,
  options?: TimeSinceOptions,
): string | null => {
  const [, forceRefresh] = useState(0);
  const hasDate = date != null;

  useEffect(() => {
    if (!hasDate) return;
    return subscribe(() => forceRefresh((tick) => tick + 1));
  }, [hasDate]);

  return date ? timeSince(new Date(date), options) : null;
};
